import React from 'react';
import { OptionType, MultiSelectMenu } from '@fullstory/components';
import { useAsyncFilter, useFilter } from './useFilter';

/**
 * Hook to create a menu filter.
 * Consumers of BulkActionTable should use this to
 * create a menu filter and pass it into the filters prop.
 * @param key React key used for rendering.
 * @param initialState The initial selected option in the menu.
 * @param predicate Function to filter a single row. Returns true if the row should be rendered given the filter, false if not.
 * @param options List of options to show in the menu.
 */
export function useMultiFilter<R, F>(
  key: string,
  initialState: OptionType<F>[],
  predicate: (row: R, filter: OptionType<F>[]) => boolean,
  options: OptionType<F>[],
) {
  return useFilter(
    key,
    initialState,
    predicate,
    React.useCallback(
      props => (
        <MultiSelectMenu
          selected={props.filter}
          options={options}
          onChange={props.setFilter}
          variant="fill-builder"
          key={key}
        />
      ),
      [key, options],
    ),
  );
}

/**
 * Hook to create a menu filter.
 * Consumers of BulkActionTable should use this to
 * create a menu filter and pass it into the filters prop.
 * @param key React key used for rendering.
 * @param initialState The initial selected option in the menu.
 * @param filterRows Function to asynchronously filter the set of rows given the selected option.
 * @param options List of options to show in the menu.
 */
export function useAsyncMultiFilter<R, F>(
  key: string,
  initialState: OptionType<F>[],
  filterRows: (rows: R[], filter: OptionType<F>[]) => Promise<R[]>,
  options: OptionType<F>[],
) {
  return useAsyncFilter(
    key,
    initialState,
    filterRows,
    React.useCallback(
      props => (
        <MultiSelectMenu
          selected={props.filter}
          options={options}
          onChange={props.setFilter}
          variant="fill-builder"
          key={key}
        />
      ),
      [key, options],
    ),
  );
}
